/**
 * TikTok's "Required UX Implementation" as rules, so the share sheet stays a view.
 *
 * The parts TikTok insists on: the host picks the privacy level themselves and nothing is
 * preselected, the interaction settings start unchecked, the commercial disclosure starts off and
 * needs an answer once it is on, a paid partnership can never be private, a clip may not run past
 * the account's limit, and the consent sentence names the policies that apply.
 */
import type { TikTokOptions, TikTokPrivacyLevel } from '@str/shared';

/** What TikTok says this creator may choose, as the app's API hands it back. */
export type TikTokCreator = {
  nickname: string | null;
  username: string | null;
  avatarUrl: string | null;
  privacyOptions: string[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoSeconds: number | null;
};

/** What the host has ticked so far. Nothing is chosen for them. */
export type TikTokDraft = {
  privacy: TikTokPrivacyLevel | null;
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  disclose: boolean;
  yourBrand: boolean;
  brandedContent: boolean;
  /** TikTok wants an express act, so this is a tick the host makes, never a default. */
  consented: boolean;
};

export const EMPTY_TIKTOK_DRAFT: TikTokDraft = {
  privacy: null,
  allowComment: false,
  allowDuet: false,
  allowStitch: false,
  disclose: false,
  yourBrand: false,
  brandedContent: false,
  consented: false,
};

/**
 * What the host agreed to. Change any of it and the old tick no longer covers the new post, so
 * the consent goes back and has to be given again. Interaction settings are not part of it.
 */
const AGREED_ON = ['privacy', 'disclose', 'yourBrand', 'brandedContent'] as const;

/** Applies a change to the draft, taking the consent back when the agreement itself changed. */
export function nextTikTokDraft(prev: TikTokDraft, patch: Partial<TikTokDraft>): TikTokDraft {
  const next = { ...prev, ...patch };
  const changed = AGREED_ON.some((k) => next[k] !== prev[k]);
  return changed ? { ...next, consented: false } : next;
}

/**
 * TikTok answers with no privacy options when it will not take a post from this account at the
 * moment. It does not say why, so neither do we.
 */
export function tiktokOffline(creator: TikTokCreator): boolean {
  return creator.privacyOptions.length === 0;
}

const MUSIC_URL = 'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en';
const BRANDED_URL = 'https://www.tiktok.com/legal/page/global/bc-policy/en';
const PRIVATE_BLOCKED = 'Branded content visibility cannot be set to private';

/** TikTok's own words for each level, so the host reads what TikTok will do. */
export const TIKTOK_PRIVACY_LABEL: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Everyone',
  MUTUAL_FOLLOW_FRIENDS: 'Friends (people you follow back)',
  FOLLOWER_OF_CREATOR: 'Followers',
  SELF_ONLY: 'Only you',
};

/** A paid partnership must be visible to someone, so "Only you" goes away when it is ticked. */
export function tiktokPrivacyChoices(
  creator: TikTokCreator,
  draft: TikTokDraft,
): { value: TikTokPrivacyLevel; label: string; disabled: boolean; why: string | null }[] {
  const paid = draft.disclose && draft.brandedContent;
  return creator.privacyOptions.map((o) => ({
    value: o as TikTokPrivacyLevel,
    label: TIKTOK_PRIVACY_LABEL[o] ?? o,
    disabled: paid && o === 'SELF_ONLY',
    why: paid && o === 'SELF_ONLY' ? PRIVATE_BLOCKED : null,
  }));
}

/** Why the post cannot go out yet, in the host's words, or null when it can. */
export function tiktokBlocker(
  draft: TikTokDraft,
  creator: TikTokCreator,
  clipSeconds: number | null,
): string | null {
  // Nothing the host can tick helps if TikTok is not taking posts from the account at all.
  if (tiktokOffline(creator))
    return 'TikTok is not accepting posts from this account at the moment. You can try again later, or take TikTok off this post.';
  // The clip itself comes first: no choice the host makes can fix it, so say it straight away.
  const limit = creator.maxVideoSeconds;
  if (limit && clipSeconds && clipSeconds > limit)
    return `This clip runs ${Math.round(clipSeconds)} seconds and TikTok takes up to ${limit} on this account.`;
  if (!draft.privacy) return 'Choose who can see it on TikTok first.';
  if (draft.disclose && !draft.yourBrand && !draft.brandedContent)
    return 'Say whether this post promotes you, someone else, or both.';
  if (draft.disclose && draft.brandedContent && draft.privacy === 'SELF_ONLY')
    return `${PRIVATE_BLOCKED}.`;
  if (!draft.consented) return 'Tick that you agree to TikTok’s rules for this post.';
  return null;
}

/** The answers to send, or null while something is still missing. */
export function tiktokOptionsFrom(
  draft: TikTokDraft,
  creator: TikTokCreator,
): TikTokOptions | null {
  if (tiktokOffline(creator)) return null;
  if (!draft.privacy) return null;
  if (draft.disclose && !draft.yourBrand && !draft.brandedContent) return null;
  if (!draft.consented) return null;
  return {
    consented: true,
    privacyLevel: draft.privacy,
    allowComment: draft.allowComment && !creator.commentDisabled,
    allowDuet: draft.allowDuet && !creator.duetDisabled,
    allowStitch: draft.allowStitch && !creator.stitchDisabled,
    // Turning the disclosure off takes back both answers, so we never claim one by accident.
    yourBrand: draft.disclose && draft.yourBrand,
    brandedContent: draft.disclose && draft.brandedContent,
  };
}

/** The label TikTok will put on the post, when the host disclosed anything. */
export function tiktokLabelNote(draft: TikTokDraft): string | null {
  if (!draft.disclose) return null;
  // TikTok: both options selected still reads as a paid partnership.
  if (draft.brandedContent) return 'Your video will be labeled as “Paid partnership”.';
  if (draft.yourBrand) return 'Your video will be labeled as “Promotional content”.';
  return null;
}

/** TikTok's required consent sentence, naming only the policies that apply. */
export function tiktokConsent(draft: TikTokDraft): {
  text: string;
  links: { label: string; href: string }[];
} {
  const music = { label: 'Music Usage Confirmation', href: MUSIC_URL };
  if (draft.disclose && draft.brandedContent)
    return {
      text: 'By posting, you agree to TikTok’s Branded Content Policy and Music Usage Confirmation.',
      links: [{ label: 'Branded Content Policy', href: BRANDED_URL }, music],
    };
  return { text: 'By posting, you agree to TikTok’s Music Usage Confirmation.', links: [music] };
}
